// src/components/league/StartLeaguePanel.tsx — commissioner affordance for
// starting a league that has enough teams to play but fewer than it planned for.
//
// Five real leagues sat in `setup` for weeks holding 2-5 teams against a cap of
// 4-10. The Start Draft control already existed and already allowed it; nothing
// told the commissioner they did not have to wait for a full house, and the
// league read "5 / 10" the whole time. This says it plainly and spells out what
// starting costs, because starting cannot be undone.
import React, { useState } from 'react';

interface StartLeaguePanelProps {
  teamCount: number;
  maxTeams: number | null;
  onStart: () => void;
  loading: boolean;
  error: string | null;
}

export default function StartLeaguePanel({
  teamCount,
  maxTeams,
  onStart,
  loading,
  error,
}: StartLeaguePanelProps) {
  const [confirming, setConfirming] = useState(false);
  const underFilled = maxTeams !== null && teamCount < maxTeams;
  const odd = teamCount % 2 === 1;

  return (
    <>
      <div className="mb-5 border-2 border-ink-900 bg-secondary-50 p-4">
        <p className="font-display uppercase text-ink-900 text-lg leading-none">
          Ready when you are
        </p>
        <p className="text-sm text-secondary-700 mt-2">
          {underFilled
            ? `${teamCount} teams have joined of the ${maxTeams} you planned for. You do not have to wait for the rest — a league plays fine at this size.`
            : `All ${teamCount} teams have joined.`}
        </p>
        <button
          onClick={() => setConfirming(true)}
          className="btn-primary w-full mt-3 uppercase tracking-wider"
        >
          Start Draft With {teamCount} Teams
        </button>
        <p className="text-xs text-secondary-500 mt-2">
          Locks the league at {teamCount}
          {odd ? '; one team sits out each week' : ''}. No one can join after this.
        </p>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-md border border-secondary-300 bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="start-draft-heading"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !loading) setConfirming(false);
            }}
          >
            <h3
              id="start-draft-heading"
              className="font-display uppercase text-xl leading-none text-ink-900"
            >
              Start the draft
            </h3>
            <p className="mt-3 text-sm text-secondary-700">
              {teamCount} teams are in. Starting the draft locks the league at that size
              {odd ? ' and, with an odd number of teams, one team sits out each week' : ''}.
              Nobody can join once it begins.
            </p>
            {error && (
              <p className="mt-3 text-sm text-danger-700" role="alert">
                {error}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="btn-outline-secondary flex-1"
                onClick={() => setConfirming(false)}
                disabled={loading}
              >
                Not yet
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={onStart}
                disabled={loading}
                autoFocus
              >
                {loading ? 'Starting…' : 'Start Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
